import Grid from '@mui/material/Grid2';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import ButtonComponent from '#app/Component/ButtonComponent';
import MultipleSelectComponent from '#app/Component/MultipleSelectComponent';
import ShadowBoxComponent from '#app/Component/ShadowBoxComponent';
import TextFieldComponent from '#app/Component/TextFieldComponent';
import { Form, initFormHandler } from '#app/Handler/FormHandler';
import { generateApiPath } from '#app/Handler/api';
import { StateType } from '#app/Store';
import { Filters } from '#app/View/Sale/Client/SaleClientList';
import ClientEntryPoints from '#app/entrypoints/client';
import useShops from '#app/hooks/useShops';
import { ChannelClient, ChannelClientSelects } from '#app/typings/channelClient';
import { UserFunction } from '#app/typings/user';

type Props = {
  handleSearch: (data: Filters) => void;
  filters: Filters;
};

const Search: React.FC<Props> = ({ handleSearch, filters }) => {
  const classes = useStyles();
  const { shops } = useShops();
  const authenticationReducer = useSelector((state: StateType) => state.AuthenticationReducer);
  const [loadingExport, setLoadingExport] = useState<boolean>(false);
  const [withShops, setWithShops] = useState<boolean>(
    authenticationReducer.userFunction === UserFunction.MANAGER_SELLER,
  );

  const [formSearch, setFormSearch] = useState<Form>({
    number: {
      name: 'number',
      label: 'Référence',
      textHelper: 'Rechercher par référence.',
      type: 'text',
      defaultValue: filters?.number,
    },
    email: {
      name: 'email',
      label: 'Email',
      textHelper: 'Rechercher par email.',
      type: 'text',
      defaultValue: filters?.email,
    },
    firstname: {
      name: 'firstname',
      label: 'Prénom',
      textHelper: 'Rechercher par prénom.',
      type: 'text',
      defaultValue: filters?.firstname,
    },
    lastname: {
      name: 'lastname',
      label: 'Nom',
      textHelper: 'Rechercher par nom.',
      type: 'text',
      defaultValue: filters?.lastname,
    },
    zipCode: {
      name: 'zipCode',
      label: 'Code postal',
      textHelper: 'Rechercher par code postal.',
      type: 'text',
      defaultValue: filters?.zipCode,
    },
    ...(authenticationReducer.userFunction !== UserFunction.MANAGER_SELLER && {
      channels: {
        name: 'channels',
        label: 'Canal',
        textHelper: 'Rechercher par canaux.',
        type: 'array',
        defaultValue: filters?.channels,
      },
    }),
    shops: {
      name: 'shops',
      label: 'Boutique',
      textHelper: 'Rechercher par boutiques.',
      type: 'array',
      defaultValue: filters?.shops,
    },
  });

  const handlerForm = initFormHandler(formSearch, setFormSearch);
  useEffect(() => {
    handlerForm.start();
  }, []);

  const getData = (): Filters => {
    const data = handlerForm.getData();
    return {
      number: data.number,
      email: data.email,
      firstname: data.firstname,
      lastname: data.lastname,
      zipCode: data.zipCode,
      channels: data.channels,
      shops: data.shops,
    };
  };

  const download = () => {
    setLoadingExport(true);

    const aElement = document.createElement('a');
    aElement.href = generateApiPath(ClientEntryPoints.getCollectionDownload, {}, getData());
    document.body.appendChild(aElement);
    aElement.click();
    document.body.removeChild(aElement);

    setTimeout(() => setLoadingExport(false), 10000);
  };

  return (
    <ShadowBoxComponent className={classes.container}>
      <Grid container>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <TextFieldComponent id="number" handler={handlerForm} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <TextFieldComponent id="email" handler={handlerForm} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <TextFieldComponent id="firstname" handler={handlerForm} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <TextFieldComponent id="lastname" handler={handlerForm} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <TextFieldComponent id="zipCode" handler={handlerForm} />
        </Grid>
        {authenticationReducer.userFunction !== UserFunction.MANAGER_SELLER && (
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <MultipleSelectComponent
              withSelectAll
              id="channels"
              options={ChannelClientSelects}
              handler={handlerForm}
              onChange={(value) => {
                const withShops = (value as unknown as string[]).includes(ChannelClient.TLN.toString());
                setWithShops(withShops);
                if (!withShops) {
                  handlerForm.setValue('shops', []);
                }
              }}
            />
          </Grid>
        )}
        {withShops && (
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <MultipleSelectComponent id="shops" options={shops} handler={handlerForm} withSelectAll />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <ButtonComponent className={classes.button} label="Rechercher" onClick={() => handleSearch(getData())} />
          <ButtonComponent
            className={classes.button}
            label="Telecharger"
            onClick={() => download()}
            disabled={loadingExport}
            loading={loadingExport}
          />
        </Grid>
      </Grid>
    </ShadowBoxComponent>
  );
};

const useStyles = makeStyles({
  container: {
    marginBottom: 20,
  },
  button: {
    margin: '6px !important',
  },
});

export default Search;
